import React, { useEffect, useState } from 'react';
import { api, formatError } from '../api';
import { X, ChevronDown, ChevronUp } from 'lucide-react';

export default function NewDealModal({ onClose, onCreated }) {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({
    company: '', contact_name: '', contact_email: '', contact_phone: '',
    contact_type: 'GC', source: 'Cold Outreach',
    sales_rep_name: '',
    notes: '',
    // optional fields (filled in later when estimator is assigned)
    project_name: '', project_address: '', project_size: '', estimating_scope: '',
    stage: 'new_lead',
  });
  const [showProject, setShowProject] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/meta').then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const payload = {
        ...form,
        client_quote: 0,
        estimator_cost: 0,
        owner_id: null,
        contact_email: form.contact_email || null,
      };
      const { data } = await api.post('/deals', payload);
      onCreated?.(data);
    } catch (e) { setErr(formatError(e)); }
    setBusy(false);
  };

  return (
    <div className="crm-modal-backdrop" onClick={onClose}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: '92vh', overflowY: 'auto' }} data-testid="crm-new-deal-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <h2>New Lead</h2>
            <p style={{ color: 'var(--crm-muted)', fontSize: 13, margin: '2px 0 0' }}>
              Capture the contractor's basic info. Project details can be added later when drawings arrive.
            </p>
          </div>
          <button onClick={onClose} className="crm-btn ghost sm" type="button"><X size={14} /></button>
        </div>

        {err && <div style={{ background: 'var(--crm-rose-soft)', color: '#9f1239', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 10 }}>{err}</div>}

        <form onSubmit={submit}>
          {/* Essentials */}
          <div className="crm-grid-2">
            <div>
              <label className="crm-label">Company / Contractor *</label>
              <input required className="crm-input" value={form.company}
                onChange={(e) => set('company', e.target.value)} data-testid="new-deal-company"
                placeholder="ACME Builders LLC" />
            </div>
            <div>
              <label className="crm-label">Contractor type</label>
              <select className="crm-select" value={form.contact_type} onChange={(e) => set('contact_type', e.target.value)}>
                {(meta?.contact_types || ['GC', 'SC', 'MEP', 'HVAC']).map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="crm-label">Contact name</label>
              <input className="crm-input" value={form.contact_name}
                onChange={(e) => set('contact_name', e.target.value)} placeholder="John Smith" />
            </div>
            <div>
              <label className="crm-label">Lead source</label>
              <select className="crm-select" value={form.source} onChange={(e) => set('source', e.target.value)}>
                {(meta?.lead_sources || ['Manual', 'Cold Outreach', 'Referral']).map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="crm-label">Contact email</label>
              <input type="email" className="crm-input" value={form.contact_email}
                onChange={(e) => set('contact_email', e.target.value)} placeholder="contact@acme.com" />
            </div>
            <div>
              <label className="crm-label">Contact phone</label>
              <input className="crm-input" value={form.contact_phone}
                onChange={(e) => set('contact_phone', e.target.value)} placeholder="(555) 123-4567" />
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <label className="crm-label">Sales rep (your name) *</label>
            <input
              required className="crm-input" value={form.sales_rep_name}
              onChange={(e) => set('sales_rep_name', e.target.value)}
              placeholder="e.g. Sarah Johnson"
              data-testid="new-deal-rep-name"
            />
            <p style={{ fontSize: 11, color: 'var(--crm-muted)', margin: '4px 0 0' }}>
              Type the name of the rep who is bringing this lead in.
            </p>
          </div>

          <div style={{ marginTop: 10 }}>
            <label className="crm-label">Initial notes / interest level</label>
            <textarea className="crm-textarea" value={form.notes} onChange={(e) => set('notes', e.target.value)}
              placeholder="e.g. Said no current project but want to be contacted in 60 days — works on TI projects 5k–30k SF."
              data-testid="new-deal-notes" />
          </div>

          {/* Optional project info — collapsed by default */}
          <button
            type="button"
            onClick={() => setShowProject((s) => !s)}
            className="crm-btn ghost"
            style={{ marginTop: 12, width: '100%', justifyContent: 'space-between', padding: '10px 14px', fontSize: 12.5 }}
          >
            <span>{showProject ? 'Hide' : 'Add'} project info (optional — can be filled later)</span>
            {showProject ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {showProject && (
            <div style={{ marginTop: 10, background: 'var(--crm-indigo-soft)', padding: 12, borderRadius: 10 }}>
              <p style={{ fontSize: 12, color: 'var(--crm-muted)', margin: '0 0 10px', fontStyle: 'italic' }}>
                You only need this once the contractor has shared drawings. You can leave it blank for now and fill it in on the deal page later.
              </p>
              <div className="crm-grid-2">
                <div><label className="crm-label">Project name</label><input className="crm-input" value={form.project_name} onChange={(e) => set('project_name', e.target.value)} /></div>
                <div><label className="crm-label">Project address</label><input className="crm-input" value={form.project_address} onChange={(e) => set('project_address', e.target.value)} /></div>
                <div><label className="crm-label">Approx. SF / size</label><input className="crm-input" value={form.project_size} onChange={(e) => set('project_size', e.target.value)} placeholder="28,000 SF" /></div>
                <div><label className="crm-label">Estimating scope</label><input className="crm-input" value={form.estimating_scope} onChange={(e) => set('estimating_scope', e.target.value)} placeholder="All 16 CSI Divisions" /></div>
              </div>
            </div>
          )}

          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="crm-btn ghost">Cancel</button>
            <button type="submit" className="crm-btn" disabled={busy} data-testid="new-deal-submit">
              {busy ? 'Saving…' : 'Add Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
